import React, { useState } from "react";

const CustomEmailSender = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [responseType, setResponseType] = useState("");
  
  const studentId = localStorage.getItem("student_id");

  const emailTemplates = [
    {
      name: "Select a template...",
      subject: "",
      message: ""
    },
    {
      name: "Welcome Message",
      subject: "Welcome to Our Institution",
      message: "We are excited to welcome you to our institution. Your journey with us begins now, and we're here to support you every step of the way."
    },
    {
      name: "Document Request",
      subject: "Additional Documents Required",
      message: "We need some additional documents to complete your application. Please submit the required documents at your earliest convenience."
    },
    {
      name: "Interview Reminder",
      subject: "Interview Reminder - Tomorrow",
      message: "This is a friendly reminder about your scheduled interview tomorrow. Please arrive 15 minutes early and bring the required documents."
    },
    {
      name: "Application Status Update",
      subject: "Application Status Update",
      message: "We wanted to update you on the status of your application. Your application is currently under review and you will hear from us soon."
    },
    {
      name: "Congratulations",
      subject: "Congratulations! Application Approved",
      message: "Congratulations! We are pleased to inform you that your application has been approved. Welcome to our institution family!"
    }
  ];

  const showResponse = (text, type = "info") => {
    setResponse(text);
    setResponseType(type);
    setTimeout(() => {
      setResponse("");
      setResponseType("");
    }, 5000);
  };

  const handleTemplateChange = (e) => {
    const selectedTemplate = emailTemplates[e.target.value];
    if (selectedTemplate && e.target.value !== "0") {
      setSubject(selectedTemplate.subject);
      setMessage(selectedTemplate.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studentId) {
      showResponse("Student ID not found. Please log in again.", "error");
      return;
    }

    if (!subject || !message) {
      showResponse("Please fill in subject and message fields.", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-custom-email/${studentId}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}` 
        },
        body: JSON.stringify({ subject, message }),
      });

      const data = await res.json();

      if (res.ok) {
        showResponse(`Email sent successfully to ${data.data.to}`, "success");
        setSubject("");
        setMessage("");
      } else {
        showResponse(data.message || "Failed to send email.", "error");
      }
    } catch (err) {
      showResponse("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSubject("");
    setMessage("");
    setResponse("");
    setResponseType("");
  };

  const getResponseStyles = () => {
    const baseStyles = "p-3 rounded text-sm font-medium mb-4 border";
    switch (responseType) {
      case "success":
        return `${baseStyles} bg-green-50 text-green-800 border-green-200`;
      case "error":
        return `${baseStyles} bg-red-50 text-red-800 border-red-200`;
      default:
        return `${baseStyles} bg-blue-50 text-blue-800 border-blue-200`;
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-blue-600 px-4 py-4">
        <div className="flex items-center space-x-2">
          <div className="bg-white bg-opacity-20 rounded p-2">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Send Email</h2>
            <p className="text-blue-100 text-xs">Send emails to students</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Response Message */}
        {response && (
          <div className={getResponseStyles()}>
            <div className="flex items-center space-x-2">
              {responseType === "success" && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {responseType === "error" && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <span className="text-sm">{response}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Template Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quick Templates
            </label>
            <select
              onChange={handleTemplateChange}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {emailTemplates.map((template, index) => (
                <option key={index} value={index}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={6}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-vertical"
              required
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                Auto-formatted with greeting and signature
              </p>
              <span className="text-xs text-gray-400">
                {message.length} chars
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <button
              type="submit"
              disabled={loading || !studentId || !subject || !message}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Send Email</span>
                </div>
              )}
            </button>
            
            <button
              type="button"
              onClick={handleClear}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500"
            >
              Clear
            </button>
          </div>
        </form>

        {/* Preview Section */}
        {(subject || message) && (
          <div className="mt-6 p-4 bg-gray-50 rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
            <div className="bg-white p-3 rounded border text-sm">
              <div className="border-b pb-2 mb-2">
                <strong>Subject:</strong> {subject}
              </div>
              <div className="space-y-1">
                <p><strong>Dear Student,</strong></p>
                <p className="whitespace-pre-wrap">{message}</p>
                <p className="mt-2 text-xs">
                  <em>Best regards,<br />GCRD</em>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomEmailSender;